import React, { useCallback } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Button,
} from '@mui/material';
import { Close, ContentCopy } from '@mui/icons-material';
import QRCode from 'qrcode.react';

type EsimQrCodeDialogProps = {
  open: boolean;
  onClose: () => void;
  activationCode?: string | null;
  confirmationCode?: string | null;
  title?: string;
  onCopy?: (label: string, value: string) => void | Promise<void>;
};

const dashIfEmpty = (v: unknown) => {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'string') return v.trim() ? v : '-';
  return String(v);
};

const isNonEmptyString = (v: unknown): v is string =>
  typeof v === 'string' && v.trim().length > 0;

const EsimQrCodeDialog = ({
  open,
  onClose,
  activationCode,
  confirmationCode,
  title = 'eSIM QR Code',
  onCopy,
}: EsimQrCodeDialogProps) => {
  const activation = activationCode?.trim() ?? '';
  const confirmation = confirmationCode?.trim() ?? '';

  const copyValue = useCallback(
    async (label: string, value: string) => {
      const text = value.trim();
      if (!text) return;

      if (onCopy) {
        await onCopy(label, text);
        return;
      }

      await navigator.clipboard.writeText(text);
    },
    [onCopy],
  );

  const renderCopyAdornment = (label: string, value?: string | null) => {
    const enabled = isNonEmptyString(value);

    return (
      <InputAdornment position="end">
        <Tooltip title={enabled ? `Copy ${label}` : 'No value'}>
          <span>
            <IconButton
              size="small"
              edge="end"
              disabled={!enabled}
              onClick={() => copyValue(label, value ?? '')}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </InputAdornment>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="esim-qr-dialog-title"
    >
      <DialogTitle id="esim-qr-dialog-title" sx={{ pr: 6, fontWeight: 900 }}>
        {title}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="Close"
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={(theme) => ({
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor:
              theme.palette.mode === 'light'
                ? theme.palette.grey[50]
                : theme.palette.grey[900],
          })}
        >
          {activation ? <QRCode value={activation} size={320} /> : null}
        </Box>

        <Box mt={2}>
          <TextField
            label="Activation Code"
            value={dashIfEmpty(activationCode)}
            fullWidth
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: renderCopyAdornment('Activation Code', activationCode),
            }}
          />
        </Box>

        {confirmation ? (
          <Box mt={2}>
            <TextField
              label="Confirmation Code"
              value={confirmation}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: renderCopyAdornment('Confirmation Code', confirmationCode),
              }}
            />
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EsimQrCodeDialog;
